import React, { useEffect, useState } from "react";
import axios from "axios";
import { Save, RefreshCw, Phone, Mail, MapPin, Image, Share2, Plus, Trash2, FileText } from "lucide-react";

const ManageFooterSection = () => {
  const [formData, setFormData] = useState({
    isActive: true,
    contactInfo: [],
    middleSection: {
      backgroundImage: "",
      logo: "",
      description: "",
      socialSection: {
        title: "",
        description: "",
        socialIcons: []
      }
    },
    copyrightText: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  // Fetch Data
  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get("https://allyoutube-01-02d59d78beb5.herokuapp.com/api/footer-section");
      setFormData(res.data.data);
    } catch (error) {
      setMessage({ text: "Error loading data", type: "danger" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Handle Input Change for simple fields
  const handleChange = (e, section, subSection, field) => {
    const { value } = e.target;
    setFormData((prev) => {
      const newData = { ...prev };
      if (section && subSection) {
        newData[section][subSection][field] = value;
      } else if (section) {
        newData[section][field] = value;
      } else {
        newData[field] = value;
      }
      return newData;
    });
  };

  // Handle Contact Info Array
  const handleContactChange = (index, field, value) => {
    const updatedContacts = [...formData.contactInfo];
    updatedContacts[index][field] = value;
    setFormData((prev) => ({ ...prev, contactInfo: updatedContacts }));
  };

  const addContact = () => {
    setFormData((prev) => ({
      ...prev,
      contactInfo: [...prev.contactInfo, { icon: "", label: "" }],
    }));
  };

  const removeContact = (index) => {
    setFormData((prev) => ({
      ...prev,
      contactInfo: prev.contactInfo.filter((_, i) => i !== index),
    }));
  };

  // Handle Social Icons Array
  const handleSocialChange = (index, field, value) => {
    const updatedSocials = [...formData.middleSection.socialSection.socialIcons];
    updatedSocials[index][field] = value;
    setFormData((prev) => ({
      ...prev,
      middleSection: {
        ...prev.middleSection,
        socialSection: {
          ...prev.middleSection.socialSection,
          socialIcons: updatedSocials
        }
      }
    }));
  };

  const addSocial = () => {
    setFormData((prev) => ({
      ...prev,
      middleSection: {
        ...prev.middleSection,
        socialSection: {
          ...prev.middleSection.socialSection,
          socialIcons: [...prev.middleSection.socialSection.socialIcons, { name: "", url: "#", icon: "" }],
        }
      }
    }));
  };

  const removeSocial = (index) => {
    setFormData((prev) => ({
      ...prev,
      middleSection: {
        ...prev.middleSection,
        socialSection: {
          ...prev.middleSection.socialSection,
          socialIcons: prev.middleSection.socialSection.socialIcons.filter((_, i) => i !== index),
        }
      }
    }));
  };

  // Submit Data
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put("https://allyoutube-01-02d59d78beb5.herokuapp.com/api/footer-section", formData);
      setMessage({ text: "Footer updated successfully!", type: "success" });
    } catch (error) {
      setMessage({ text: "Failed to update footer.", type: "danger" });
    } finally {
      setSaving(false);
    }
  };

  // Reset Data
  const handleReset = async () => {
    if (window.confirm("Are you sure you want to reset to default settings?")) {
      try {
        await axios.post("https://allyoutube-01-02d59d78beb5.herokuapp.com/api/footer-section/reset");
        fetchData();
        setMessage({ text: "Reset to defaults.", type: "info" });
      } catch (error) {
        setMessage({ text: "Reset failed.", type: "danger" });
      }
    }
  };

  if (loading) return <div className="text-center mt-5">Loading Admin Panel...</div>;

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bold"><FileText className="me-2" size={28} /> Manage Footer</h2>
        <button className="btn btn-outline-secondary" onClick={handleReset}>
          <RefreshCw size={18} className="me-1" /> Reset Defaults
        </button>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type} alert-dismissible fade show`} role="alert">
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage({ text: "", type: "" })}></button>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        
        {/* Top Contact Info */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card shadow-sm">
              <div className="card-header bg-white d-flex justify-content-between align-items-center fw-bold">
                <span><Phone className="me-2" size={20} /> Contact Info (Top)</span>
                <button type="button" className="btn btn-sm btn-primary" onClick={addContact}>
                  <Plus size={16} className="me-1"/> Add Item
                </button>
              </div>
              <div className="card-body">
                {formData.contactInfo.map((item, index) => (
                  <div key={index} className="card bg-light mb-2 p-2 border-0">
                    <div className="d-flex justify-content-between mb-2">
                      <small className="text-muted">Item #{index + 1}</small>
                      <button type="button" className="btn btn-sm btn-outline-danger py-0" onClick={() => removeContact(index)}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <div className="row g-2">
                      <div className="col-md-4">
                        <input type="text" className="form-control form-control-sm" placeholder="Icon URL" value={item.icon} onChange={(e) => handleContactChange(index, "icon", e.target.value)} />
                      </div>
                      <div className="col-md-8">
                        <input type="text" className="form-control form-control-sm" placeholder="Label Text (e.g. Phone: ...)" value={item.label} onChange={(e) => handleContactChange(index, "label", e.target.value)} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Middle Section (Left: Logo & Desc) */}
        <div className="row mb-4">
          <div className="col-lg-6 mb-3">
            <div className="card shadow-sm h-100">
              <div className="card-header bg-darkfw-bold"><Image className="me-2" size={20} /> Logo & Description</div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label text-dark small">Logo URL</label>
                  <input type="text" className="form-control" value={formData.middleSection.logo} onChange={(e) => handleChange(e, "middleSection", null, "logo")} />
                </div>
                <div className="mb-3">
                  <label className="form-label text-dark small">Background Image</label>
                  <input type="text" className="form-control" value={formData.middleSection.backgroundImage} onChange={(e) => handleChange(e, "middleSection", null, "backgroundImage")} />
                </div>
                <div className="mb-0">
                  <label className="form-label text-dark small">Description</label>
                  <textarea className="form-control" rows="6" value={formData.middleSection.description} onChange={(e) => handleChange(e, "middleSection", null, "description")}></textarea>
                </div>
              </div>
            </div>
          </div>

          {/* Middle Section (Right: Social) */}
          <div className="col-lg-6 mb-3">
            <div className="card shadow-sm h-100">
              <div className="card-header bg-white d-flex justify-content-between align-items-center fw-bold">
                <span><Share2 className="me-2" size={20} /> Social Section</span>
                <button type="button" className="btn btn-sm btn-primary" onClick={addSocial}>
                  <Plus size={16} className="me-1"/> Add Icon
                </button>
              </div>
              <div className="card-body">
                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <label className="form-label text-dark small">Social Title</label>
                    <input type="text" className="form-control" value={formData.middleSection.socialSection.title} onChange={(e) => handleChange(e, "middleSection", "socialSection", "title")} />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label text-dark small">Social Description</label>
                  <textarea className="form-control" rows="2" value={formData.middleSection.socialSection.description} onChange={(e) => handleChange(e, "middleSection", "socialSection", "description")}></textarea>
                </div>
                <label className="form-label text-dark small fw-bold">Social Icons List</label>
                <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                  {formData.middleSection.socialSection.socialIcons.map((social, index) => (
                    <div key={index} className="card bg-light mb-2 p-2 border-0">
                      <div className="d-flex justify-content-between mb-1">
                        <small className="text-muted">Icon #{index + 1}</small>
                        <button type="button" className="btn btn-sm btn-outline-danger py-0" onClick={() => removeSocial(index)}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                      <div className="row g-1">
                        <div className="col-4">
                          <input type="text" className="form-control form-control-sm" placeholder="Name" value={social.name} onChange={(e) => handleSocialChange(index, "name", e.target.value)} />
                        </div>
                        <div className="col-4">
                          <input type="text" className="form-control form-control-sm" placeholder="URL" value={social.url} onChange={(e) => handleSocialChange(index, "url", e.target.value)} />
                        </div>
                        <div className="col-4">
                          <input type="text" className="form-control form-control-sm" placeholder="Icon Img" value={social.icon} onChange={(e) => handleSocialChange(index, "icon", e.target.value)} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Copyright */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card shadow-sm">
              <div className="card-header bg-darkfw-bold">Footer Bottom</div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label text-dark">Copyright Text (HTML allowed)</label>
                  <input type="text" className="form-control" value={formData.copyrightText} onChange={(e) => handleChange(e, null, null, "copyrightText")} />
                  <div className="form-text">Example: 2026 &copy; YouTube AI All rights reserved.</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2">
          <button type="submit" className="btn btn-success px-4" disabled={saving}>
            {saving ? "Saving..." : <><Save size={18} className="me-2" /> Save Changes</>}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ManageFooterSection;